import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CubedConfigResource, CubedResource } from '../../types';
import generateQueryKey from '../helpers/generate-query-key';
import { generateUrlDetail } from '../../helpers/request-builder';
import axios from 'axios';

type UseDeleteResourceArgs = {
    resource: CubedResource | CubedConfigResource;
    resourceIds: string[];
    handleOnSuccess?: () => void;
    handleOnError?: () => void;
};

const useDeleteResource = ({ resource, resourceIds, handleOnSuccess, handleOnError }: UseDeleteResourceArgs) => {
    const queryClient = useQueryClient();
    const queryKey = generateQueryKey({ resource });

    const mutation = useMutation({
        mutationFn: async () => {
            const requests = resourceIds.map(resourceId => {
                const url = generateUrlDetail(resource.category, resource.id, resourceId);

                if (url) {
                    return axios({
                        method: 'DELETE',
                        url: url,
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }).catch(error => {
                        throw new Error(error.response.data.message);
                    });
                }
                throw new Error('Error when generating url');
            });

            await Promise.all(requests);
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: queryKey,
                })
                .then(() => {
                    handleOnSuccess && handleOnSuccess();
                });
        },
        onError: handleOnError,
    });

    return mutation;
};

export default useDeleteResource;
